import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { createFaq, updateFaq } from "../../services/faq";
import CustomSnackbar from "../../components/customSnackBar/customSnackBar";
import AppLoader from "../../components/loader/loader";

const AddDialog = ({
  title,
  type,
  open,
  onClose,
  setFormData,
  formData,
  editData,
}) => {
  const [loading, setLoading] = useState(false);
  const snackRef = useRef();

  useEffect(() => {
    if (editData) {
      setFormData(editData.formData);
    } else {
      setFormData({ question: "", answer: "" });
    }
  }, [editData, setFormData]);

  const handleOpenSnack = (status, message) => {
    if (snackRef.current) {
      snackRef.current.isOpenSnack({
        snackType: status,
        snackMessage: message,
      });
    }
  };

  const handleSubmit = async () => {
    if (!formData.question || !formData.answer) {
      handleOpenSnack("error", "Please fill all fields");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        question: formData.question,
        answer: formData.answer,
        appType: type,
      };

      const response = editData?._id
        ? await updateFaq(payload, editData._id)
        : await createFaq(payload);

      if (response.data.status === "success") {
        handleOpenSnack("success", response.data.message);
        onClose(true);
      } else {
        handleOpenSnack("error", response.data.message);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      handleOpenSnack("error", error?.response?.data?.message || "An error occurred while saving");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AppLoader isVisible={loading} />
      <Box>
        <Dialog
          open={open}
          onClose={() => onClose(false)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 400,
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
            },
          }}
        >
          {/* Dialog Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              padding: 1,
              borderBottom: "1px solid #eee",
              fontSize: "1.8rem",
              fontWeight: "bold",
            }}
          >
            <Typography
              sx={{
                color: "primary.main",
                paddingLeft: "10px",
                fontWeight: "600",
                fontSize: "24px",
              }}
            >
              {editData?._id ? "Edit" : "Add"} FAQ
            </Typography>

            <IconButton onClick={() => onClose(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Dialog Content */}
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <TextField
                label={"Question"}
                value={formData.question}
                onChange={(e) =>
                  setFormData({ ...formData, question: e.target.value })
                }
                fullWidth
                disabled={loading}
              />
              <TextField
                label={"Answer"}
                value={formData.answer}
                onChange={(e) =>
                  setFormData({ ...formData, answer: e.target.value })
                }
                fullWidth
                disabled={loading}
              />
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              padding: 2,
              gap: 1,
              justifyContent: "flex-end",
            }}
          >
            <Button
              onClick={() => onClose(false)}
              disabled={loading}
              sx={{
                width: "70px",
                border: 1,
                borderColor: "primary.main",
                color: "gray",
                "&:hover": {
                  color: "black",
                },
                transition: "all 0.3s ease-in-out",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                width: "70px",

                backgroundColor: "primary.main",
                color: "white",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
                transition: "all 0.3s ease-in-out",
              }}
            >
              {editData?._id ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>
        <CustomSnackbar ref={snackRef} />
      </Box>
    </>
  );
};

export default AddDialog;
